'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import { useUser } from '@/app/model/stores';
import { useJobQueueStore } from '@/features/design/job-queue';
import { RenderStatus } from '@/shared/constants/status';
import {
  isRenderWaiting,
  isRenderProcessing,
  isRenderCompleted,
  isRenderFailed,
  calculateTimeBasedProgress,
} from '@/shared/lib/render-status';
import { getProgressAnchor } from '@/shared/lib/progress';
import {
  payloadString,
  pStr,
} from '@/shared/api/websocket/realtime-sync.payload';
import type { Render } from '@/types/render';
import type { Control } from '@/types/concept';

export interface UseRenderProgressOptions {
  render?: Render;
  control?: Control;
  mode?: 'render' | 'control';
  onComplete?: () => void;
  onError?: (error: string) => void;
  onProgressUpdate?: (progress: number) => void;
}

export type RemainingTime =
  | { type: 'seconds'; count: number }
  | { type: 'minutes'; count: number };

export interface RenderProgressState {
  progress: number;
  status: string;
  error: string | null;
  isQueued: boolean;
  isProcessing: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  isRetrying: boolean;
  isStalling: boolean;
  attemptNumber: number;
  remainingTime: RemainingTime | null;
  startedAt: string | null;
  durationPredicted: number;
}

export function useRenderProgress({
  render,
  control,
  onComplete,
  onError,
  onProgressUpdate,
}: UseRenderProgressOptions): RenderProgressState {
  const user = useUser();
  const getJobById = useJobQueueStore((state) => state.getJobById);
  const getControlById = useJobQueueStore((state) => state.getControlById);
  const queueJob = render ? getJobById(render.id) : undefined;
  const queueControl = control ? getControlById(control.id) : undefined;

  // Local state only for control-specific logic and errors
  const [error, setError] = useState<string | null>(null);

  // Local tick state for progress recalculation (only when PROCESSING)
  const [tick, setTick] = useState(0);

  // Get entity details
  const entity = render || control;

  // Use store state as single source of truth for both renders and controls
  const currentStatus = render
    ? queueJob?.status || RenderStatus.PENDING
    : queueControl?.status ||
      control?.attributes.status ||
      RenderStatus.PENDING;

  // Use store data when available
  const startedAt = render ? queueJob?.startedAt : queueControl?.startedAt;

  const durationPredicted = render ? queueJob?.durationPredicted || 90 : 45;

  const attemptNumber = render ? queueJob?.attemptNumber || 1 : 1;

  // Local interval for progress updates (NO Store mutations)
  useEffect(() => {
    if (currentStatus === RenderStatus.PROCESSING && startedAt) {
      const interval = setInterval(() => {
        setTick((t) => t + 1); // Trigger recalculation
      }, 500); // 500ms for smoother progress

      return () => clearInterval(interval);
    }
    return undefined;
  }, [currentStatus, startedAt]);

  // Calculate progress locally (NO Store involved)
  const progress = useMemo(() => {
    if (currentStatus !== RenderStatus.PROCESSING || !startedAt) return 0;

    return calculateTimeBasedProgress(startedAt, durationPredicted, render?.id);
  }, [currentStatus, startedAt, durationPredicted, tick, render?.id]); // tick triggers recalculation every 500ms

  // Set error from entity
  useEffect(() => {
    if (entity) {
      const entityAttrs = entity.attributes as { error_message?: string };
      setError(entityAttrs.error_message || null);
    }
  }, [entity]);

  // Listen for render events via new unified system
  useEffect(() => {
    if (!user || !render) return;

    const cleanup = onInstant('renders', (event) => {
      if (event.id !== render.id) return;

      if (event.action === 'completed') {
        onComplete?.();
      }

      if (event.action === 'failed') {
        const payload = event.payload;
        const errorMsg =
          payloadString(payload, 'error') ||
          pStr(payload, 'message', 'Render failed. Please try again.');
        setError(errorMsg);
        onError?.(errorMsg);
      }
    });

    return cleanup;
  }, [user, render, onComplete, onError]);

  // Listen for control events via new unified system
  useEffect(() => {
    if (!user || !control) return;

    const cleanup = onInstant('controls', (event) => {
      if (event.id !== control.id) return;

      if (event.action === 'completed') {
        onComplete?.();
      }

      if (event.action === 'failed') {
        const payload = event.payload;
        const errorMsg =
          payloadString(payload, 'error') ||
          pStr(payload, 'message', 'Control generation failed.');
        setError(errorMsg);
        onError?.(errorMsg);
      }
    });

    return cleanup;
  }, [user, control, onComplete, onError]);

  // Notify parent about progress updates
  useEffect(() => {
    onProgressUpdate?.(progress);
  }, [progress, onProgressUpdate]);

  // Calculate remaining time using monotonic anchor (immune to clock skew)
  // Mirrors Android JobProgressUtils.getRemainingTime() approach
  const getRemainingTime = useCallback((): RemainingTime | null => {
    if (currentStatus !== RenderStatus.PROCESSING) return null;

    // Use monotonic anchor for consistency with progress bar calculation
    const anchor = render?.id ? getProgressAnchor(render.id) : undefined;
    let remainingSeconds: number;

    if (anchor && anchor.durationPredicted > 0) {
      const progressFraction = progress / 100;
      remainingSeconds = Math.max(
        0,
        Math.round((1 - progressFraction) * anchor.durationPredicted)
      );
    } else if (startedAt) {
      // Fallback to wall clock if no anchor
      const startTime =
        typeof startedAt === 'string'
          ? new Date(startedAt).getTime()
          : Date.now();
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      remainingSeconds = Math.max(0, durationPredicted - elapsedSeconds);
    } else {
      return null;
    }

    if (remainingSeconds > 60) {
      return { type: 'minutes', count: Math.ceil(remainingSeconds / 60) };
    }
    if (remainingSeconds > 0) {
      return { type: 'seconds', count: remainingSeconds };
    }
    return null;
  }, [currentStatus, startedAt, durationPredicted, progress, render?.id, tick]); // tick triggers recalculation for time updates

  const isQueued = isRenderWaiting(currentStatus);
  const isProcessing = isRenderProcessing(currentStatus);

  const isStalling = isProcessing && progress >= 95;

  return {
    progress,
    status: currentStatus,
    error,
    isQueued,
    isProcessing,
    isCompleted: isRenderCompleted(currentStatus),
    isFailed: isRenderFailed(currentStatus),
    isRetrying: attemptNumber > 1,
    isStalling,
    attemptNumber,
    remainingTime: getRemainingTime(),
    startedAt: startedAt || null,
    durationPredicted,
  };
}
